/**
 * Admin Email Suppressions Hook
 * Fetch paginated email suppression list for admin UI.
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export type EmailSuppressionReason = 'hard_bounce' | 'complaint' | 'manual';

export interface EmailSuppressionItem {
  id: string;
  email: string;
  reason: EmailSuppressionReason;
  detail: string | null;
  source: string;
  createdAt: string;
}

export interface UseAdminEmailSuppressionsParams {
  page: number;
  limit: number;
}

export interface UseAdminEmailSuppressionsResult {
  suppressions: EmailSuppressionItem[];
  total: number;
  pages: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAdminEmailSuppressions(
  params: UseAdminEmailSuppressionsParams,
): UseAdminEmailSuppressionsResult {
  const { authFetch } = useAuth();
  const [suppressions, setSuppressions] = useState<EmailSuppressionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSuppressions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });

      const res = await authFetch(apiUrl(`/api/admin/email-suppressions?${queryParams}`));
      if (!res.ok) {
        throw new Error('Failed to fetch email suppressions');
      }
      const data = await res.json();
      setSuppressions(data.items);
      setTotal(data.total);
      // The API returns { items, total }; derive page count from the limit.
      setPages(Math.max(1, Math.ceil(data.total / params.limit)));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, params.page, params.limit]);

  useEffect(() => {
    fetchSuppressions();
  }, [fetchSuppressions]);

  return { suppressions, total, pages, loading, error, refetch: fetchSuppressions };
}
